import { useState, useEffect, useMemo } from "react";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";

import {
  CheckCircle,
  Trash2,
  AlertTriangle,
} from "lucide-react";

import { UnfulfilledRolesTable } from "@/components/blocks/unfulfilled-roles-table";

import { DataTableEnhanced, type ColumnDef } from '@/components/ui/data-table-enhanced';
import { PaginationControls } from '@/components/pagination-controls';

import {
  mockTeamBookingStatuses,
  mockUnbookedTimesheetersCount,
  mockUnbookedTimesheetersNames,
  type TeamMemberBookingStatus,
} from "@/lib/mock-scheduling-data";
import { fetchTeamBookingStatuses } from "@/services/schedulingService";
import { useDataMode } from "@/contexts/DataModeContext";

import { useProjectDetailsContext } from "../context";
import { usePermissions } from '@/contexts/PermissionsContext';
import type { TeamMember } from "../types";

export function TeamTab() {
  const ctx = useProjectDetailsContext();
  const { isTabEditable } = usePermissions();
  // Destructure all needed values from context
  const {
    project, employees,
    numericProjectId, setActiveTab,
    enrichedBudgetData,
    handleAssignResources,
    resourceAllocations, allResourceAllocations,
    schedulerConnected,
    setTimeTabPrefilterNames,
    handleRemoveTeamMember,
    getStatusBadgeVariant, getInitials,
    // Additional items that may be used
    teamPagination, paginatedTeamMembers,
    teamColumnSorts, setTeamColumnSorts, setTeamPage, setTeamPageSize,
  } = ctx;

  const { isLiveMode } = useDataMode();
  const projectId = project?.id != null ? String(project.id) : null;

  // Fetch live booking statuses when in live mode and scheduler is connected
  const [liveBookingStatuses, setLiveBookingStatuses] = useState<TeamMemberBookingStatus[] | null>(null);

  useEffect(() => {
    if (!projectId || !schedulerConnected) return;
    let cancelled = false;

    fetchTeamBookingStatuses(projectId)
      .then((data) => {
        if (!cancelled) setLiveBookingStatuses(data);
      })
      .catch((err) => {
        console.error('[TeamTab] Error fetching booking statuses:', err);
      });

    return () => { cancelled = true; };
  }, [projectId, schedulerConnected]);

  // Use live data in live mode, mock data in demo/tour
  const bookingStatuses = isLiveMode ? (liveBookingStatuses ?? []) : mockTeamBookingStatuses;
  const unbookedCount = useMemo(
    () => bookingStatuses.filter((m) => m.bookingStatus === 'NoBooking').length,
    [bookingStatuses],
  );
  const unbookedNames = useMemo(
    () => bookingStatuses.filter((m) => m.bookingStatus === 'NoBooking').map((m) => m.personName),
    [bookingStatuses],
  );

  if (!project) return null;

  return (
    <>
                {/* Resourcing warning: unbooked timesheeters — shown when scheduler is connected */}
                {schedulerConnected && unbookedCount > 0 && (
                  <div className="flex items-start gap-3 rounded-md border border-warning-200 bg-warning-50 p-3 text-sm text-warning-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-500" />
                    <div className="flex-1">
                      <span className="font-medium">
                        {unbookedCount === 1
                          ? '1 person has'
                          : `${unbookedCount} people have`}{' '}
                        logged time on this project without a scheduler booking.
                      </span>{' '}
                      This may indicate untracked resource demand.
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0 text-warning-700 hover:text-warning-900 hover:bg-warning-100 h-auto p-0"
                      onClick={() => {
                        setTimeTabPrefilterNames(unbookedNames);
                        setActiveTab('time');
                      }}
                    >
                      View in Time →
                    </Button>
                  </div>
                )}

                {/* Unfulfilled Roles Table — hidden when scheduler connected (Resource Budget tab handles requests) */}
                {enrichedBudgetData?.resourceBudget?.byRole && project && !schedulerConnected && (
                  <UnfulfilledRolesTable
                    roles={enrichedBudgetData.resourceBudget.byRole}
                    projectId={numericProjectId}
                    projectStartDate={project.startDate}
                    projectEndDate={project.endDate}
                    availablePeople={employees}
                    existingAllocations={allResourceAllocations}
                    onAssign={handleAssignResources}
                  />
                )}

                {/* Team Management Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Team Management</CardTitle>

                  </CardHeader>
                  <CardContent className="p-0">
                    {(() => {
                      const getAvatarColorClass = (name: string) => {
                        const colors = [
                          "bg-teal-100 text-teal-700",
                          "bg-success-100 text-success-700",
                          "bg-error-100 text-error-700",
                          "bg-warning-100 text-warning-700",
                          "bg-purple-100 text-purple-700",
                          "bg-info-100 text-info-700",
                          "bg-indigo-100 text-indigo-700",
                          "bg-cyan-100 text-cyan-700",
                        ];
                        const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
                        return colors[hash % colors.length];
                      };

                      const teamColDefs: ColumnDef<TeamMember, unknown>[] = [
                        {
                          id: 'avatar',
                          header: '',
                          enableSorting: false,
                          cell: ({ row }: { row: { original: TeamMember } }) => (
                            <Avatar className="h-8 w-8 rounded-md">
                              <AvatarFallback className={`text-xs rounded-md ${getAvatarColorClass(row.original.name)}`}>
                                {getInitials(row.original.name)}
                              </AvatarFallback>
                            </Avatar>
                          ),
                        },
                        {
                          id: 'name',
                          header: 'Name',
                          accessorKey: 'name',
                          cell: ({ row }: { row: { original: TeamMember } }) => <span className="font-medium">{row.original.name}</span>,
                        },
                        { id: 'role', header: 'Role', accessorKey: 'role' },
                        {
                          id: 'allocatedHours',
                          header: 'Allocated Hrs',
                          accessorKey: 'allocatedHours',
                          cell: ({ row }: { row: { original: TeamMember } }) => (
                            <span className="font-sans">
                              {row.original.allocatedHours === 0
                                ? <span className="text-muted-foreground italic">Not allocated</span>
                                : `${row.original.allocatedHours.toFixed(0)}h`}
                            </span>
                          ),
                        },
                        {
                          id: 'actualHours',
                          header: 'Actual Hrs',
                          accessorKey: 'actualHours',
                          cell: ({ row }: { row: { original: TeamMember } }) => <span className="font-sans">{row.original.actualHours.toFixed(2)}h</span>,
                        },
                        {
                          id: 'remaining',
                          header: 'Remaining',
                          accessorKey: 'remaining',
                          cell: ({ row }: { row: { original: TeamMember } }) => (
                            <span className={`font-sans ${row.original.remaining < 0 ? 'text-error-600' : ''}`}>
                              {row.original.remaining.toFixed(2)}h
                            </span>
                          ),
                        },
                        {
                          id: 'status',
                          header: 'Status',
                          accessorKey: 'status',
                          enableSorting: false,
                          cell: ({ row }: { row: { original: TeamMember } }) => (
                            <Badge variant={getStatusBadgeVariant(row.original.status, row.original.utilisationPercent)}>
                              {row.original.status}
                            </Badge>
                          ),
                        },
                        ...(schedulerConnected ? [{
                          id: 'bookingStatus',
                          header: 'Booking Status',
                          enableSorting: false,
                          cell: ({ row }: { row: { original: TeamMember } }) => {
                            const bookingStatus = bookingStatuses.find(
                              (b) => b.personName === row.original.name,
                            )?.bookingStatus ?? 'NoBooking';
                            const pillStyle = bookingStatus === 'Confirmed'
                              ? 'bg-success-100 text-success-700 border border-success-200'
                              : bookingStatus === 'Planned'
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'bg-warning-100 text-warning-700 border border-warning-200';
                            const label = bookingStatus === 'Confirmed' ? 'Confirmed'
                              : bookingStatus === 'Planned' ? 'Planned' : 'No Booking';
                            const pillIcon = bookingStatus === 'Confirmed'
                              ? <CheckCircle className="h-3 w-3 shrink-0" />
                              : bookingStatus === 'Planned'
                                ? <span className="h-2 w-2 shrink-0 rounded-full bg-blue-500 inline-block" />
                                : <AlertTriangle className="h-3 w-3 shrink-0" />;
                            return (
                              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${pillStyle}`}>
                                {pillIcon}{label}
                              </span>
                            );
                          },
                        } as ColumnDef<TeamMember, unknown>] : []),
                        {
                          id: 'actions',
                          header: '',
                          enableSorting: false,
                          cell: ({ row }: { row: { original: TeamMember } }) => (
                            row.original.actualHours === 0 && isTabEditable('team') ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                      onClick={() => handleRemoveTeamMember(row.original)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent><p>Remove from project</p></TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : null
                          ),
                        },
                      ];

                      return (
                        <DataTableEnhanced
                          columns={teamColDefs}
                          data={paginatedTeamMembers}
                          columnVisibility={Object.fromEntries(teamColDefs.map(c => [c.id!, true]))}
                          columnSorts={teamColumnSorts}
                          onColumnSortChange={(s) => { setTeamColumnSorts(s); setTeamPage(1); }}
                          emptyMessage={<span className="text-muted-foreground">No team members found</span>}
                          className="rounded-none [&_th:first-child]:border-r-0 [&_th:last-child]:pr-0 [&_td:last-child]:pr-0"
                          paginationContent={
                            <PaginationControls
                              pagination={teamPagination}
                              onPageChange={setTeamPage}
                              onPageSizeChange={(s) => { setTeamPageSize(s); setTeamPage(1); }}
                            />
                          }
                        />
                      );
                    })()}
                  </CardContent>
                </Card>

    </>
  );
}
